// SPDX-FileCopyrightText: 2017-2025 City of Espoo
//
// SPDX-License-Identifier: LGPL-2.1-or-later

import { useQueryClient } from '@tanstack/react-query'
import isEqual from 'lodash/isEqual'
import orderBy from 'lodash/orderBy'
import uniqBy from 'lodash/uniqBy'
import React, {
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useState,
  useRef
} from 'react'
import styled from 'styled-components'

import type {
  ApplicationSummary,
  ApplicationSummaryPlacementDraft,
  PagedApplicationSummaries,
  PreferredUnit
} from 'lib-common/generated/api-types/application'
import type { UnitStub } from 'lib-common/generated/api-types/daycare'
import type {
  ApplicationId,
  DaycareId
} from 'lib-common/generated/api-types/shared'
import LocalDate from 'lib-common/local-date'
import { useQueryResult } from 'lib-common/query'
import type { Arg0 } from 'lib-common/types'
import Combobox from 'lib-components/atoms/dropdowns/Combobox'
import Spinner from 'lib-components/atoms/state/Spinner'
import {
  FixedSpaceColumn,
  FixedSpaceRow
} from 'lib-components/layout/flex-helpers'
import { AlertBox } from 'lib-components/molecules/MessageBoxes'
import DatePicker from 'lib-components/molecules/date-picker/DatePicker'
import { defaultMargins, Gap } from 'lib-components/white-space'

import { unitsQuery } from '../../../queries'
import { ApplicationUIContext } from '../../../state/application-ui'
import { useTranslation } from '../../../state/i18n'
import { renderResult } from '../../async-rendering'
import {
  getApplicationSummariesQuery,
  getPlacementDesktopDaycareQuery,
  getPlacementDesktopDaycaresQuery
} from '../queries'

import ApplicationCard from './ApplicationCard'
import DaycareCard from './DaycareCard'

export default React.memo(function PlacementDesktop({
  applicationSummaries
}: {
  applicationSummaries: PagedApplicationSummaries
}) {
  const { i18n } = useTranslation()

  const allUnits = useQueryResult(unitsQuery({}))

  if (applicationSummaries.pages > 1 || applicationSummaries.total > 50) {
    return (
      <AlertBox
        title={i18n.applications.placementDesktop.warnings.tooManyApplicationsTitle(
          applicationSummaries.total
        )}
        message={
          i18n.applications.placementDesktop.warnings.tooManyApplicationsMessage
        }
      />
    )
  }

  return renderResult(allUnits, (allUnits) => (
    <PlacementDesktopValidated
      applications={applicationSummaries.data}
      allUnits={allUnits}
    />
  ))
})

const PlacementDesktopValidated = React.memo(
  function PlacementDesktopValidated({
    applications,
    allUnits
  }: {
    applications: ApplicationSummary[]
    allUnits: UnitStub[]
  }) {
    const { i18n, lang } = useTranslation()
    const queryClient = useQueryClient()

    const {
      confirmedSearchFilters,
      placementDesktopDaycares: shownDaycares,
      setPlacementDesktopDaycares: _setShownDaycares,
      occupancyPeriodStart,
      setOccupancyPeriodStart
    } = useContext(ApplicationUIContext)
    const searchedUnits = useMemo(
      () =>
        allUnits.filter((u1) =>
          (confirmedSearchFilters?.units ?? []).some((u2) => u1.id === u2)
        ),
      [allUnits, confirmedSearchFilters?.units]
    )

    // optimistic cache to avoid refetching all applications when updating placements drafts
    const [placementDraftCache, setPlacementDraftCache] = useState<
      Record<ApplicationId, ApplicationSummaryPlacementDraft | undefined>
    >({})

    const daycareListRef = useRef<HTMLDivElement>(null)
    const [daycareRefs, setDaycareRefs] = useState<
      Record<DaycareId, React.RefObject<HTMLDivElement>>
    >({})

    // set this to scroll to a specific daycare and trigger an animation
    // as soon as it's visible and a ref is available
    const [highlightedDaycare, setHighlightedDaycareDaycare] =
      useState<DaycareId | null>(null)
    const resetHighlightRef = useRef<ReturnType<typeof setTimeout> | null>(null)
    useEffect(() => {
      // useEffect is needed to wait for the DOM to update
      if (
        highlightedDaycare &&
        daycareRefs[highlightedDaycare]?.current &&
        daycareListRef.current
      ) {
        const daycareElement = daycareRefs[highlightedDaycare].current
        const daycareListElement = daycareListRef.current
        daycareListElement.scrollTo({
          top: daycareElement.offsetTop - daycareListElement.offsetTop - 10,
          behavior: 'smooth'
        })
      }

      // reset the highlight after the animation has run, so that it can be retriggered
      if (resetHighlightRef.current) {
        clearTimeout(resetHighlightRef.current)
      }
      if (highlightedDaycare) {
        resetHighlightRef.current = setTimeout(() => {
          setHighlightedDaycareDaycare(null)
        }, 1000)
      }

      return () => {
        if (resetHighlightRef.current) {
          clearTimeout(resetHighlightRef.current)
        }
      }
    }, [highlightedDaycare, daycareRefs])

    const setShownDaycares = useCallback(
      (units: PreferredUnit[]) => {
        _setShownDaycares(units)

        setDaycareRefs((prev) =>
          units.reduce(
            (acc, daycare) => ({
              ...acc,
              [daycare.id]:
                prev[daycare.id] ?? React.createRef<HTMLDivElement>()
            }),
            {}
          )
        )
      },
      [_setShownDaycares]
    )

    const otherAvailableUnits = useMemo(
      () =>
        allUnits.filter(
          (u) => !shownDaycares || !shownDaycares.some((u2) => u.id === u2.id)
        ),
      [allUnits, shownDaycares]
    )

    useEffect(() => {
      setPlacementDraftCache(
        applications.reduce(
          (acc, application) => ({
            ...acc,
            [application.id]: application.placementDraft
          }),
          {}
        )
      )
    }, [applications])

    useEffect(() => {
      if (shownDaycares !== undefined) return

      // By default, show daycares that are
      // - one of the searched units, or
      // - a preferred unit of some result application, or
      // - already have a placement draft from some result application
      setShownDaycares(
        orderBy(
          uniqBy(
            [
              ...searchedUnits,
              ...applications.map((a) => a.preferredUnits[0]),
              ...applications.flatMap((a) =>
                a.placementDraft ? [a.placementDraft.unit] : []
              )
            ],
            (u) => u.id
          ),
          (u) => u.name
        )
      )
    }, [applications, searchedUnits, shownDaycares, setShownDaycares])

    const onUpsertApplicationPlacementSuccess = useCallback(
      (
        applicationId: ApplicationId,
        unit: PreferredUnit,
        startDate: LocalDate
      ) => {
        setPlacementDraftCache((prev) => ({
          ...prev,
          [applicationId]: { unit, startDate }
        }))
      },
      []
    )

    const onDeleteApplicationPlacementSuccess = useCallback(
      (applicationId: ApplicationId) => {
        setPlacementDraftCache((prev) => ({
          ...prev,
          [applicationId]: undefined
        }))
      },
      []
    )

    const onMutateApplicationPlacementFailure = useCallback(() => {
      void queryClient.invalidateQueries({
        queryKey: getApplicationSummariesQuery.prefix,
        type: 'all'
      })
    }, [queryClient])

    const onAddToShownDaycares = useCallback(
      (unit: PreferredUnit) => {
        if (shownDaycares) {
          setShownDaycares(
            orderBy(
              [...shownDaycares.filter((u) => u.id !== unit.id), unit],
              (u) => u.name
            )
          )
        }
      },
      [shownDaycares, setShownDaycares]
    )

    const onRemoveFromShownDaycares = useCallback(
      (unitId: DaycareId) => {
        if (shownDaycares) {
          setShownDaycares(shownDaycares.filter((u) => u.id !== unitId))
        }
      },
      [shownDaycares, setShownDaycares]
    )

    return (
      <FixedSpaceColumn spacing="L">
        <FixedSpaceRow alignItems="center">
          <div>{i18n.applications.placementDesktop.occupancyPeriod}:</div>
          <DatePicker
            data-qa="occupancy-period-start-picker"
            date={occupancyPeriodStart}
            onChange={(date) =>
              setOccupancyPeriodStart(date ?? LocalDate.todayInHelsinkiTz())
            }
            locale={lang}
          />
          <div>—</div>
          <div data-qa="occupancy-period-end">
            {occupancyPeriodStart.addMonths(3).format()}
          </div>
        </FixedSpaceRow>
        <ColumnsContainer spacing="L">
          <DaycaresColumn ref={daycareListRef}>
            <div>
              {i18n.applications.placementDesktop.shownUnitsCount}:{' '}
              {shownDaycares?.length}
            </div>
            <Gap size="xs" />
            <Combobox
              items={otherAvailableUnits}
              selectedItem={null}
              onChange={(unit) => unit && onAddToShownDaycares(unit)}
              placeholder={i18n.applications.placementDesktop.addShownUnit}
              getItemLabel={(unit) => unit.name}
              fullWidth
            />
            <Gap size="s" />
            {shownDaycares !== undefined && (
              <PrefetchedDaycares
                shownDaycares={shownDaycares}
                daycareRefs={daycareRefs}
                highlightedDaycare={highlightedDaycare}
                applications={applications}
                onDeleteApplicationPlacementSuccess={
                  onDeleteApplicationPlacementSuccess
                }
                onMutateApplicationPlacementFailure={
                  onMutateApplicationPlacementFailure
                }
                onRemoveFromShownDaycares={onRemoveFromShownDaycares}
              />
            )}
          </DaycaresColumn>

          <ApplicationsColumn>
            <div style={{ textAlign: 'right' }}>
              {i18n.applications.placementDesktop.applicationsCount}:{' '}
              {applications.length}
            </div>
            <Gap size="s" />
            <ApplicationsList>
              {shownDaycares !== undefined &&
                applications.map((application) => (
                  <ApplicationCard
                    key={application.id}
                    application={{
                      ...application,
                      placementDraft:
                        placementDraftCache[application.id] ?? null
                    }}
                    shownDaycares={shownDaycares}
                    allUnits={allUnits}
                    onUpsertApplicationPlacementSuccess={
                      onUpsertApplicationPlacementSuccess
                    }
                    onDeleteApplicationPlacementSuccess={
                      onDeleteApplicationPlacementSuccess
                    }
                    onMutateApplicationPlacementFailure={
                      onMutateApplicationPlacementFailure
                    }
                    onAddOrHighlightDaycare={(unit) => {
                      onAddToShownDaycares(unit)
                      setHighlightedDaycareDaycare(unit.id)
                    }}
                  />
                ))}
            </ApplicationsList>
          </ApplicationsColumn>
        </ColumnsContainer>
      </FixedSpaceColumn>
    )
  }
)

const PrefetchedDaycares = React.memo(function PrefetchedDaycares({
  shownDaycares,
  daycareRefs,
  highlightedDaycare,
  applications,
  onDeleteApplicationPlacementSuccess,
  onMutateApplicationPlacementFailure,
  onRemoveFromShownDaycares
}: {
  shownDaycares: PreferredUnit[]
  daycareRefs: Record<DaycareId, React.RefObject<HTMLDivElement | null>>
  highlightedDaycare: DaycareId | null
  applications: ApplicationSummary[]
  onDeleteApplicationPlacementSuccess: (applicationId: ApplicationId) => void
  onMutateApplicationPlacementFailure: () => void
  onRemoveFromShownDaycares: (unitId: DaycareId) => void
}) {
  const queryClient = useQueryClient()
  const { occupancyPeriodStart } = useContext(ApplicationUIContext)

  // do not refetch when shownDaycares change, unless applications also change
  // to avoid refetching everything when adding/removing a single shown daycare
  const queryArg: Arg0<typeof getPlacementDesktopDaycaresQuery> = useMemo(
    () => ({
      unitIds: shownDaycares.map((d) => d.id),
      occupancyStart: occupancyPeriodStart
    }),
    [occupancyPeriodStart, applications] // eslint-disable-line react-hooks/exhaustive-deps
  )

  const initialData = useQueryResult(getPlacementDesktopDaycaresQuery(queryArg))

  const [initialDataInsertedForArgs, setInitialDataInsertedForArgs] =
    useState<Arg0<typeof getPlacementDesktopDaycaresQuery> | null>(null)

  useEffect(() => {
    if (initialData.isSuccess && !initialData.isReloading) {
      initialData.value.forEach((unit) => {
        void queryClient.setQueryData(
          getPlacementDesktopDaycareQuery({
            unitId: unit.id,
            occupancyStart: queryArg.occupancyStart
          }).queryKey,
          unit
        )
      })
      setInitialDataInsertedForArgs(queryArg)
    }
  }, [initialData, queryArg, queryClient])

  const initialDataUpToDate = isEqual(queryArg, initialDataInsertedForArgs)

  if (!initialDataUpToDate || !initialData.isSuccess || initialData.isReloading)
    return <Spinner />

  return (
    <FixedSpaceColumn>
      {shownDaycares.map((daycare) => (
        <DaycareCard
          key={daycare.id}
          daycare={daycare}
          highlighted={daycare.id === highlightedDaycare}
          applications={applications}
          onDeleteApplicationPlacementSuccess={
            onDeleteApplicationPlacementSuccess
          }
          onMutateApplicationPlacementFailure={
            onMutateApplicationPlacementFailure
          }
          onRemoveFromShownDaycares={() =>
            onRemoveFromShownDaycares(daycare.id)
          }
          ref={daycareRefs[daycare.id]}
        />
      ))}
    </FixedSpaceColumn>
  )
})

const ColumnsContainer = styled(FixedSpaceRow)`
  min-width: 0;
  align-items: flex-start;
`

const DaycaresColumn = styled.div`
  width: 450px;
  flex-shrink: 0;
  padding: ${defaultMargins.xxs};
  padding-right: ${defaultMargins.s};
  max-height: 90vh;
  overflow-y: auto;

  @media (min-width: 1600px) {
    width: 550px;
  }
`

const ApplicationsColumn = styled.div`
  flex: 1;
  min-width: 0;
  padding: ${defaultMargins.xxs};
  padding-right: ${defaultMargins.s};
  max-height: 90vh;
  overflow-y: auto;
  overflow-x: hidden;
`

const ApplicationsList = styled(FixedSpaceColumn).attrs({
  justifyContent: 'stretch'
})`
  flex-grow: 1;
  width: 100%;
`
